import React from 'react'
import useStyles from './styles'
import { Lock1 } from 'iconsax-react'
import { useTheme } from '@mui/material'

import Checkbox from '../../../../../../../pages/Settings/Controls/Checkbox'
import Tooltip from '../../../../../../../components/atoms/Tooltip'
import { useRolesPermissions } from '@SonarCX/providers/RolesPermissionsProvider'

const PermissionsSection = ({ setPermissionsArray, data }) => {
  const theme = useTheme()

  const classes = useStyles()
  const { state } = useRolesPermissions()
  const { allPermissions, permissionsLoaded } = state
  const [sortedPermissions, setSortedPermissions] = React.useState({})
  const [sorted, setSorted] = React.useState(false)
  const [loaded, setLoaded] = React.useState(false)
  const [checkedArray, setCheckedArray] = React.useState([])
  const [disabled, setDisabled] = React.useState(['users.admin'])

  // being edited, set checked array to begin with filled permissions
  React.useEffect(() => {
    if (data) {
      if (!data?.permissions) {
        return
      }

      const list = data.permissions ? [...data.permissions] : []
      const disabledList = []
      // need to check for implications and check them too
      data.permissions.forEach(id => {
        const fullOpt = allPermissions[id]

        if (fullOpt?.implied.length) {
          fullOpt.implied.forEach(imp => {
            list.push(imp)
            disabledList.push(imp)
          })
        }
        // hardcoding this for now so users can't click option
        disabledList.push('users.admin')
      })

      // removing any duplicates on open edit modal
      const uniqueList = [...new Set(list)]

      // make dependencies grey
      setDisabled(disabledList)
      setCheckedArray(uniqueList)

      // disabling all checkboxes for system roles
      if (data.system) {
        const idsArray = Object.values(allPermissions).map(
          permission => permission.id
        )
        setDisabled(idsArray)
      }
    } else {
      setCheckedArray([])
    }
  }, [data, allPermissions])

  // sorting all permissions into groups
  // key is group name, value is array of objects
  React.useEffect(() => {
    if (allPermissions) {
      const toSort = { ...allPermissions }
      const groups = {}

      Object.values(toSort).forEach(obj => {
        if (!groups[obj.group]) {
          groups[obj.group] = [obj]
        } else {
          groups[obj.group] = [...groups[obj.group], obj]
        }
      })
      setSortedPermissions(groups)
      setSorted(true)
    }
  }, [allPermissions])

  // sets loading to true when permissions and load status come from provider
  React.useEffect(() => {
    if (allPermissions && permissionsLoaded) {
      setLoaded(true)
    }
  }, [state, allPermissions, permissionsLoaded])

  // adding to state
  const addingPermission = React.useCallback(
    ids => {
      setCheckedArray(permissions => {
        let add = [...permissions]

        ids.forEach(id => {
          if (!add?.includes(id)) {
            add = [...add, id]
          }
        })

        return add
      })

      // disabling implications
      ids.forEach(i => {
        const fullOpt = allPermissions[i]

        if (fullOpt.implied.length) {
          setDisabled(disabled => [...disabled, ...fullOpt.implied])
        }
      })
    },
    [allPermissions]
  )

  const enablingOptions = React.useCallback(
    id => {
      // removing currently deleted option from list to check
      const listToCheck = [...checkedArray]
      const index = listToCheck.indexOf(id)
      listToCheck.splice(index, 1)

      const disabledList = []

      // check implications to disable
      listToCheck.forEach(item => {
        const fullOption = allPermissions[item]

        if (fullOption?.implied?.length) {
          fullOption.implied.forEach(opt => {
            if (!disabledList.includes(opt)) {
              disabledList.push(opt)
            }
          })
        }
      })
      // hardcoding this option for now
      disabledList.push('users.admin')
      // returning list of dependencies after checking every parent
      setDisabled(disabledList)
    },
    [checkedArray, allPermissions]
  )

  const removingPermission = React.useCallback(
    id => {
      const toRemove = [...checkedArray]
      let index = -1

      index = toRemove.indexOf(id)

      if (index > -1) {
        toRemove.splice(index, 1)

        setCheckedArray(toRemove)
      }

      enablingOptions(id)
    },
    [checkedArray, enablingOptions]
  )

  // adding or removing from array depending on checked status
  const handleCheck = React.useCallback(
    (obj, checked) => {
      if (!checked && !obj) {
        return
      }

      if (checked) {
        // adding selected id and implications to permissions
        addingPermission([obj.id, ...obj.implied])
      } else {
        removingPermission(obj.id)
      }
    },
    [addingPermission, removingPermission]
  )

  // sending info to provider
  React.useEffect(() => {
    setPermissionsArray(checkedArray)
  }, [checkedArray, setPermissionsArray])

  return (
    <div className={classes.root}>
      <div className={classes.permissionsList}>
        {sorted &&
          loaded &&
          Object.entries(sortedPermissions).map(([key, array]) => {
            return (
              <div className={classes.groups} key={key}>
                <div className={classes.groupName}> {key}: </div>
                {array.map(obj => {
                  return (
                    <div className={classes.permission} key={obj.id}>
                      <Checkbox
                        checked={(checkedArray ?? []).includes(obj.id)}
                        disabled={(disabled ?? []).includes(obj.id)}
                        onCheck={e => handleCheck(obj, e)}
                        className={classes.checkBox}
                        defaultClass={classes.defaultClass}
                      >
                        {(disabled ?? []).includes(obj.id) ? (
                          <Tooltip
                            title='Cannot edit implied/system permission'
                            arrow
                          >
                            {obj.id === 'users.admin' ? (
                              <div className={classes.lockedOption}>
                                <Lock1
                                  size={theme.size.sm}
                                  className={classes.icon}
                                />{' '}
                                <div>{obj.name}</div>
                              </div>
                            ) : (
                              <div>{obj.name}</div>
                            )}
                          </Tooltip>
                        ) : (
                          <div>{obj.name}</div>
                        )}
                      </Checkbox>

                      <div className={classes.details}>{obj.details}</div>
                    </div>
                  )
                })}
              </div>
            )
          })}
      </div>
    </div>
  )
}

export default PermissionsSection
